/**
 * @file Drop-in replacement for antd's `Table` on record lists: at `md` and up it
 * renders `Table` with the props untouched; below `md` it renders the same
 * columns and rows as cards through `ResponsiveTableCards`. Consumers: record
 * lists outside settings and the editor (forms, archive, ledger, analytics,
 * review queues). The extra `cardTitle` prop names the column that titles each
 * card, and `cardSummary` is the cards' stand-in for `summary`, which the table
 * keeps drawing at `md` and up. Cards render only when a breakpoint below `md` is positively reported,
 * so before antd's first breakpoint dispatch, and in jsdom where no media query
 * matches, the table renders.
 */
import type { ReactNode } from 'react';
import { Grid, Table } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';

import ResponsiveTableCards from './ResponsiveTableCards';
import type { ResponsiveTableProps } from './ResponsiveTable.types';

const ResponsiveTable = <T extends AnyObject>({
  cardTitle,
  cardSummary,
  ...props
}: ResponsiveTableProps<T>): ReactNode => {
  const { xs, sm, md } = Grid.useBreakpoint();
  return !md && (xs || sm) ? (
    <ResponsiveTableCards<T> {...props} cardTitle={cardTitle} cardSummary={cardSummary} />
  ) : (
    <Table<T> {...props} />
  );
};

export default ResponsiveTable;
